import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';

import { companyApi } from '@/api';
import { resolveFileUrl } from '@/api/client';
import { Badge, Card, CardHeader, ErrorState, Icon, LoadingState } from '@/components/ui';
import { Unavailable } from '@/components/marketing/Unavailable';
import { BrandVideo } from '@/features/branding/BrandVideo';
import { TrustSignals } from '@/features/branding/TrustSignals';
import { ReportButton } from '@/features/reports/ReportButton';
import { useAuth } from '@/hooks/useAuth';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { ACCENT_VAR, accentStyle } from '@/utils/branding';
import { ApiError } from '@/types/api';
import { cn } from '@/utils/cn';

/** Two letters is enough identity for a logo tile. */
function initials(name) {
  if (!name) return '?';
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');
}

/**
 * Public company page, reachable by slug and readable while signed out.
 *
 * Two kinds of content, kept apart on purpose: what the company says about
 * itself (`branding` -- cover, tagline, mission, culture, values, perks,
 * video, gallery) and what the platform counted (`trust`). The first is
 * styled in the company's accent; the second sits in a muted card with its
 * own source line and never takes the accent.
 *
 * `branding` is null until the company fills anything in, and the page then
 * falls back to the plain About and Details it always had.
 */
export function CompanyPage() {
  const { slug } = useParams();
  const { user } = useAuth();

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['company-public', slug],
    queryFn: () => companyApi.getPublicCompany(slug),
    enabled: Boolean(slug),
  });

  if (isPending) return <LoadingState label="Loading company…" />;
  if (isError) {
    if (error instanceof ApiError && error.status === 404) {
      return (
        <Unavailable
          icon="building"
          title="This company page is not available"
          message="The link may be out of date, or the company is no longer on Join-Wonder. Open internships from other companies are a click away."
        />
      );
    }
    return (
      <div className="app-container py-12">
        <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
      </div>
    );
  }

  const branding = data.branding;
  const accent = accentStyle(branding?.accent_color);
  const gallery = [...(branding?.gallery ?? [])]
    .filter((photo) => photo.url)
    .sort((a, b) => a.position - b.position);
  const openRoles = `${data.open_internships} open ${data.open_internships === 1 ? 'role' : 'roles'}`;

  return (
    <div style={accent}>
      <CoverBand coverUrl={branding?.cover_url} hasAccent={Boolean(accent)} />

      <div className="app-container pb-12">
        <header className="border-line bg-surface rounded-card shadow-card relative -mt-12 flex flex-wrap items-end gap-5 border p-5 sm:-mt-16 sm:p-6">
          {data.logo_url ? (
            <img
              src={resolveFileUrl(data.logo_url)}
              alt={`${data.name} logo`}
              className="border-line bg-surface size-20 shrink-0 rounded-xl border object-contain"
            />
          ) : (
            <span
              aria-hidden="true"
              className="grid size-20 shrink-0 place-items-center rounded-xl text-2xl font-semibold text-white"
              style={{ background: ACCENT_VAR }}
            >
              {initials(data.name)}
            </span>
          )}

          <div className="min-w-0 flex-1">
            <h1 className="text-ink flex flex-wrap items-center gap-x-3 gap-y-1 text-2xl font-semibold tracking-tight">
              {data.name}
              {data.is_verified && (
                <Badge tone="success">
                  <Icon name="check" size={12} />
                  Verified
                </Badge>
              )}
            </h1>
            {branding?.tagline && <p className="text-ink mt-1">{branding.tagline}</p>}
            {(data.industry?.name || data.location) && (
              <p className="text-ink-muted mt-1 text-sm">
                {[data.industry?.name, data.location].filter(Boolean).join(' · ')}
              </p>
            )}
          </div>

          <Link
            // The id filters the search; the name only labels its chip.
            to={`${paths.internships}?company_id=${data.id}&company=${encodeURIComponent(data.name)}`}
            className="rounded-input inline-flex h-10 items-center justify-center gap-2 px-4 text-sm font-medium whitespace-nowrap text-white shadow-sm transition hover:brightness-110 focus-visible:outline-2 focus-visible:outline-offset-2"
            style={{ background: ACCENT_VAR, outlineColor: ACCENT_VAR }}
          >
            {openRoles}
            <Icon name="arrowRight" size={16} />
          </Link>
        </header>

        <div className="mt-6 grid items-start gap-6 lg:grid-cols-[1fr_320px]">
          <div className="min-w-0 space-y-6">
            {branding?.mission && (
              <Card>
                <CardHeader title="Mission" />
                <p className="text-ink text-[15px] leading-relaxed whitespace-pre-line">
                  {branding.mission}
                </p>
              </Card>
            )}

            {branding?.culture && (
              <Card>
                <CardHeader title="Life as an intern" />
                <p className="text-ink-muted text-sm leading-relaxed whitespace-pre-line">
                  {branding.culture}
                </p>
              </Card>
            )}

            {branding?.values?.length > 0 && (
              <Card>
                <CardHeader title="Values" />
                <ul className="grid gap-3 sm:grid-cols-2">
                  {branding.values.map((value, index) => (
                    <li
                      key={`${index}-${value.title}`}
                      className="bg-surface-muted/50 rounded-input border-l-4 px-4 py-3"
                      style={{ borderLeftColor: ACCENT_VAR }}
                    >
                      <p className="text-ink font-medium">{value.title}</p>
                      {value.description && (
                        <p className="text-ink-muted mt-1 text-sm whitespace-pre-line">
                          {value.description}
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {branding?.perks?.length > 0 && (
              <Card>
                <CardHeader title="Perks" />
                <ul className="flex flex-wrap gap-2" aria-label="Perks">
                  {branding.perks.map((perk) => (
                    <li
                      key={perk.code}
                      className="bg-surface text-ink ring-line inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm ring-1 ring-inset"
                    >
                      <span style={{ color: ACCENT_VAR }}>
                        <Icon name="check" size={14} />
                      </span>
                      {perk.label}
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {branding?.video?.embed_url && (
              <Card>
                <CardHeader title="Video" />
                <BrandVideo video={branding.video} companyName={data.name} />
              </Card>
            )}

            {gallery.length > 0 && (
              <Card>
                <CardHeader title="Gallery" />
                <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {gallery.map((photo) => (
                    <li key={photo.id}>
                      <figure>
                        <img
                          src={resolveFileUrl(photo.url)}
                          alt={photo.caption || `${data.name} photo`}
                          loading="lazy"
                          className="border-line aspect-[4/3] w-full rounded-lg border object-cover"
                        />
                        {photo.caption && (
                          <figcaption className="text-ink-muted mt-1.5 text-xs">
                            {photo.caption}
                          </figcaption>
                        )}
                      </figure>
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            <Card>
              <CardHeader title="About" />
              {data.description ? (
                <p className="text-ink-muted text-sm whitespace-pre-line">{data.description}</p>
              ) : (
                <p className="text-ink-muted text-sm">
                  This company has not written an introduction yet.
                </p>
              )}
            </Card>
          </div>

          <aside className="space-y-6">
            <TrustSignals trust={data.trust} />
            <DetailsCard company={data} />
            {/* Quiet, at the end: for students who dealt with this company. A
                company never reports itself. */}
            {user?.company?.company_id !== data.id && (
              <div className="text-center">
                <ReportButton targetType="COMPANY" targetId={data.id} targetName={data.name} />
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}

/**
 * The cover photo; the accent colour as a band when there is no photo; and a
 * quiet brand wash when the company has set neither, so the header card
 * always has something to sit on.
 */
function CoverBand({ coverUrl, hasAccent }) {
  if (coverUrl) {
    return (
      <div className="bg-surface-muted h-40 w-full overflow-hidden sm:h-64">
        <img src={resolveFileUrl(coverUrl)} alt="" className="size-full object-cover" />
      </div>
    );
  }
  return (
    <div
      aria-hidden="true"
      className={cn(
        'h-28 w-full sm:h-36',
        !hasAccent && 'from-brand-100 to-brand-50 bg-linear-to-r',
      )}
      style={hasAccent ? { background: ACCENT_VAR } : undefined}
    />
  );
}

function DetailsCard({ company }) {
  return (
    <Card>
      <CardHeader title="Details" />
      <dl className="space-y-3 text-sm">
        {company.company_size && (
          <div>
            <dt className="text-ink-muted">Company size</dt>
            <dd className="text-ink">{company.company_size} employees</dd>
          </div>
        )}
        {company.founded_year && (
          <div>
            <dt className="text-ink-muted">Founded</dt>
            <dd className="text-ink">{company.founded_year}</dd>
          </div>
        )}
        {company.website && (
          <div>
            <dt className="text-ink-muted">Website</dt>
            <dd>
              <a
                href={company.website}
                target="_blank"
                rel="noreferrer noopener"
                className="text-brand-700 break-all hover:underline"
              >
                {company.website}
              </a>
            </dd>
          </div>
        )}
        {company.linkedin_url && (
          <div>
            <dt className="text-ink-muted">LinkedIn</dt>
            <dd>
              <a
                href={company.linkedin_url}
                target="_blank"
                rel="noreferrer noopener"
                className="text-brand-700 hover:underline"
              >
                View profile
              </a>
            </dd>
          </div>
        )}
      </dl>
    </Card>
  );
}
